import { useEffect, useState } from "react";
import { useAppointments } from "@/features/appointments/hooks/appointmentQueries";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ItemList } from "@/shared/components/ui/ItemList";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { ClientAppointmentCard } from "./ClientAppointmentCard";

export function ClientAppointmentHistory({ clientId }: { clientId: string }) {
  const { businessId } = useCurrentBusiness();
  const { goToAppointmentDetails } = useAppNavigation();
  const appointmentsQuery = useAppointments();
  const servicesQuery = useServices();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  if (appointmentsQuery.isLoading || servicesQuery.isLoading) {
    return <Section title="Appointment history"><RowSkeleton /></Section>;
  }
  const error = appointmentsQuery.error || servicesQuery.error;
  if (error) {
    return (
      <Section title="Appointment history">
        <ErrorState message={error.message} onRetry={() => { void appointmentsQuery.refetch(); void servicesQuery.refetch(); }} />
      </Section>
    );
  }

  const appointments = (appointmentsQuery.data || []).filter((appointment) => appointment.clientId === clientId && appointment.businessId === businessId);
  const canceled = appointments.filter((appointment) => appointment.status === "cancelled" || appointment.status === "declined");
  const active = appointments.filter((appointment) => appointment.status !== "cancelled" && appointment.status !== "declined");
  const isPast = (appointment: typeof appointments[number]) =>
    appointment.status === "completed" || appointment.status === "no_show" || (!!appointment.startAt && appointment.startAt.getTime() < now);
  const upcoming = active.filter((appointment) => appointment.startAt && !isPast(appointment));
  const past = active.filter(isPast);
  const unscheduled = active.filter((appointment) => !appointment.startAt && !isPast(appointment));
  const ascending = (a: typeof appointments[number], b: typeof appointments[number]) =>
    (a.startAt?.getTime() || 0) - (b.startAt?.getTime() || 0);
  const sections = [
    { title: "Upcoming appointments", items: upcoming.sort(ascending), empty: "Future visits will appear here." },
    { title: "Past appointments", items: past.sort((a, b) => ascending(b, a)), empty: "There are no past visits for this customer." },
    ...(canceled.length ? [{ title: "Canceled appointments", items: canceled.sort((a, b) => ascending(b, a)), empty: "" }] : []),
    ...(unscheduled.length ? [{ title: "Unscheduled appointments", items: unscheduled, empty: "" }] : []),
  ];

  return sections.map(({ title, items, empty }) => (
    <Section key={title} title={title} count={`${items.length} ${items.length === 1 ? "appointment" : "appointments"}`}>
      <ItemList
        items={items}
        emptyState={{ message: empty }}
        renderItem={(appointment) => (
          <ClientAppointmentCard appointment={appointment} services={servicesQuery.data || []} onClick={() => goToAppointmentDetails(appointment.id, false)} />
        )}
      />
    </Section>
  ));
}
